/**
 * justJoinedOrg — short-lived marker set the moment a team invitation is
 * accepted (owner directive 2026-08-12).
 *
 * The accept page stores the workspace name here right before landing the
 * new member on /desktop, and the app-level chat greets them with a
 * personalized first message — "you're now inside <org>'s workspace" —
 * exactly the way a new organization is greeted after its own onboarding.
 *
 * TTL-scoped (10 minutes) rather than consume-on-read: the greeting composer
 * may evaluate several times while the workspace surface boots, and a
 * consumed marker would race it. After the TTL the account's normal tenant
 * welcome takes back over.
 */

const KEY = 'prest0_just_joined_org';
const MAX_AGE_MS = 10 * 60 * 1000;

export interface JustJoinedOrg {
  firmName: string;
  savedAtMs: number;
}

export function saveJustJoinedOrg(firmName: string): void {
  try {
    const name = (firmName ?? '').trim();
    if (!name) return;
    const entry: JustJoinedOrg = { firmName: name, savedAtMs: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    /* storage unavailable — the member just gets the standard welcome */
  }
}

export function getJustJoinedOrg(): JustJoinedOrg | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as JustJoinedOrg;
    if (!entry?.firmName || typeof entry.firmName !== 'string') {
      localStorage.removeItem(KEY);
      return null;
    }
    if (typeof entry.savedAtMs !== 'number' || Date.now() - entry.savedAtMs > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

export function clearJustJoinedOrg(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}
